/**
 * Injects SnapStyle widget CSS into the document head.
 * Only injects once, subsequent calls are no-ops.
 */
export function injectStyles(): void {
  if (document.querySelector("#snapstyle-styles")) return;

  const style = document.createElement("style");
  style.id = "snapstyle-styles";
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap');

    /* ── Body scroll lock ── */
    body.snapstyle-modal-open {
      overflow: hidden !important;
      position: fixed;
      width: 100%;
      height: 100%;
    }

    /* ── Modal Overlay ── */
    #snapstyle-modal {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      backdrop-filter: blur(4px);
      font-family: 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif;
      padding: 24px;
      box-sizing: border-box;
    }

    #snapstyle-modal * {
      box-sizing: border-box;
    }

    /* ── Modal Container ── */
    .snapstyle-modal-content {
      background: white;
      border-radius: 16px;
      width: 100%;
      max-width: 420px;
      height: 100%;
      overflow-y: auto;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      display: flex;
      flex-direction: column;
      position: relative;
    }

    /* Desktop: wider modal */
    @media (min-width: 768px) {
      .snapstyle-modal-content {
        max-width: 920px;
        max-height: 90vh;
      }
    }

    /* ── Close Button ── */
    .snapstyle-close-btn {
      position: absolute;
      top: 12px;
      right: 17px;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px;
      z-index: 20;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .snapstyle-close-btn svg {
      width: 20px;
      height: 20px;
    }
    .snapstyle-close-btn:hover {
      opacity: 0.7;
    }

    /* ── Back Button (top-left, mirrors close button) ── */
    .snapstyle-back-btn {
      position: absolute;
      top: 12px;
      left: 17px;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px;
      z-index: 20;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .snapstyle-back-btn svg {
      width: 20px;
      height: 20px;
    }
    .snapstyle-back-btn:hover {
      opacity: 0.7;
    }

    /* ── Screen Container ── */
    .snapstyle-screen {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    /* ═══════════════════════════════
       UPLOAD SCREEN
       ═══════════════════════════════ */

    .snapstyle-upload-screen {
      padding: 0;
    }

    .snapstyle-upload-header {
      padding: 74px 24px 24px 24px;
    }
    .snapstyle-upload-title {
      font-size: 22px;
      font-weight: 500;
      line-height: 28px;
      color: #000;
      margin: 0 0 12px 0;
    }
    .snapstyle-upload-subtitle {
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0.25px;
      color: #49454F;
      margin: 0;
    }

    /* Columns wrapper — stacks on mobile, side-by-side on desktop */
    .snapstyle-upload-columns {
      display: flex;
      flex-direction: column;
    }
    /* Hide items 5-6 on mobile (desktop-only) */
    .snapstyle-history-item--desktop-only {
      display: none;
    }

    @media (min-width: 768px) {
      /* Show all 6 items on desktop */
      .snapstyle-history-item--desktop-only {
        display: flex;
      }

      /* Stack upload + history vertically on desktop */
      .snapstyle-upload-columns {
        flex-direction: column;
        gap: 0;
        padding: 0 48px;
      }
      .snapstyle-upload-left {
        width: 100%;
      }
      .snapstyle-upload-right {
        width: 100%;
      }
      .snapstyle-upload-area {
        margin: 24px 0 !important;
        padding: 40px 48px !important;
        gap: 20px !important;
      }
      .snapstyle-upload-header {
        padding: 56px 32px 0 32px !important;
      }
      .snapstyle-upload-title {
        font-size: 28px !important;
        line-height: 34px !important;
      }
      .snapstyle-upload-subtitle {
        font-size: 16px !important;
        line-height: 24px !important;
      }
      .snapstyle-upload-label {
        font-size: 16px !important;
      }
      .snapstyle-upload-hint {
        font-size: 14px !important;
        line-height: 20px !important;
      }
      .snapstyle-btn-browse {
        height: 48px !important;
        font-size: 15px !important;
        max-width: 320px !important;
      }
      .snapstyle-btn-camera {
        display: none !important;
      }
      .snapstyle-upload-instructions {
        margin: 0 0 24px 0 !important;
        padding: 16px 20px !important;
      }
      .snapstyle-instructions-heading {
        font-size: 14px !important;
        line-height: 20px !important;
      }
      .snapstyle-instructions-text {
        font-size: 13px !important;
        line-height: 18px !important;
      }
      /* History section: full width, no side padding */
      .snapstyle-history-section {
        padding: 0 0 24px 0 !important;
      }
      .snapstyle-history-title {
        font-size: 18px !important;
      }
      /* Larger thumbnails on desktop – 6 in a row */
      .snapstyle-history-item {
        width: 110px !important;
        height: 110px !important;
      }
      .snapstyle-btn-viewall {
        height: 44px !important;
        font-size: 15px !important;
      }
    }

    /* Upload Area */
    .snapstyle-upload-area {
      margin: 0 24px;
      padding: 32px 24px;
      background-color: #F6F8FE;
      border-radius: 12px;
      border: none;
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23BFC6CC' stroke-width='1.5' stroke-dasharray='10%2c 8' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e");
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 18px;
    }
    .snapstyle-upload-icon {
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #000;
    }
    .snapstyle-upload-icon svg {
      width: 40px;
      height: 40px;
    }
    .snapstyle-upload-label {
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
      color: #000;
      margin: 0;
      text-align: center;
    }
    .snapstyle-upload-hint {
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0.4px;
      color: #938F96;
      margin: 0;
      text-align: center;
    }

    /* Browse Gallery Button (purple) */
    .snapstyle-btn-browse {
      width: 100%;
      height: 40px;
      padding: 8px;
      background: #863AFF;
      border: none;
      border-radius: 100px;
      color: white;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      transition: background 0.2s;
    }
    .snapstyle-btn-browse:hover {
      background: #7330e0;
      color: white;
    }
    .snapstyle-btn-browse svg {
      width: 16px;
      height: 16px;
    }
    .snapstyle-btn-browse-desktop {
      display: none;
    }
    @media (min-width: 768px) {
      .snapstyle-btn-browse-mobile {
        display: none;
      }
      .snapstyle-btn-browse-desktop {
        display: flex;
        align-items: center;
        gap: 5px;
      }
    }

    /* Take Photo Button (black) */
    .snapstyle-btn-camera {
      margin: 20px 24px;
      width:-webkit-fill-available;
      height: 40px;
      padding: 8px;
      background: #000;
      border: none;
      border-radius: 100px;
      color: white;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      transition: background 0.2s;
    }
    .snapstyle-btn-camera:hover {
      background: #333;
    }
    .snapstyle-btn-camera svg {
      width: 25px;
      height: 25px;
    }

    /* ── Upload Instructions Box ── */
    .snapstyle-upload-instructions {
      margin: 16px 24px 0 24px;
      padding: 14px 16px;
      background: #F8F5FE;
      border: 1px solid #E9E1FC;
      border-radius: 12px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .snapstyle-instructions-header {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .snapstyle-instructions-icon {
      width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .snapstyle-instructions-icon svg {
      width: 16px;
      height: 16px;
    }
    .snapstyle-instructions-heading {
      font-size: 13px;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: 0.1px;
      color: #3B1B7A;
      margin: 0;
    }
    .snapstyle-instructions-list {
      margin: 0;
      padding: 0;
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .snapstyle-instructions-item {
      display: flex;
      align-items: flex-start;
      gap: 8px;
    }
    .snapstyle-instructions-bullet {
      width: 14px;
      height: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-top: 1px;
    }
    .snapstyle-instructions-bullet svg {
      width: 14px;
      height: 14px;
    }
    .snapstyle-instructions-text {
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0.25px;
      color: #49454F;
      margin: 0;
    }

    /* History Section */
    .snapstyle-history-section {
      padding: 15px 24px;
    }
    @media (min-width: 768px) {
      .snapstyle-history-section {
        display: flex;
        flex-direction: column;
      }
      .snapstyle-btn-viewall{
      margin-left: auto;
      margin-right:auto;
      }
    }
    .snapstyle-history-title {
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
      letter-spacing: 0.15px;
      color: #000;
      margin: 0 0 8px 0;
    }

    /* History Empty State */
    .snapstyle-history-empty {
      padding: 32px 24px;
      background: #F6F8FE;
      border-radius: 12px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }
    .snapstyle-history-empty-icon {
      width: 40px;
      height: 40px;
      color: #000;
    }
    .snapstyle-history-empty-icon svg {
      width: 40px;
      height: 40px;
    }
    .snapstyle-history-empty-text {
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0.4px;
      color: #938F96;
      text-align: center;
      margin: 0;
    }
    @media (min-width: 768px) {
      .snapstyle-history-empty {
        padding: 85px 24px;
      }
    }

    /* History Grid (when has items) */
    .snapstyle-history-grid {
      display: flex;
      gap: 12px;
      overflow-x: auto;
      padding: 4px 0;
      -webkit-overflow-scrolling: touch;
    }
    @media (min-width: 768px) {
      .snapstyle-history-grid {
        flex-wrap: wrap;
        overflow-x: visible;
        gap: 18px;
        justify-content: flex-start;
      }
    }
    .snapstyle-history-grid::-webkit-scrollbar {
      display: none;
    }
    .snapstyle-history-item {
      flex-shrink: 0;
      width: 80px;
      height: 80px;
      border-radius: 8px;
      overflow: hidden;
      cursor: pointer;
      border: 2px solid transparent;
      transition: all 0.2s;
    }
    .snapstyle-history-item:hover {
      border-color: #863AFF;
      transform: translateY(-2px);
    }
    .snapstyle-history-item img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    /* View All History Button */
    .snapstyle-btn-viewall {
      width: 100%;
      height: 40px;
      margin-top: 12px;
      padding: 8px;
      background: white;
      border: 2px solid transparent;
      background-image: linear-gradient(white, white), linear-gradient(135deg, #6B26D9 0%, #0000FF 100%);
      background-origin: border-box;
      background-clip: padding-box, border-box;
      border-radius: 100px;
      color: #000;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      transition: all 0.2s;
    }
    .snapstyle-btn-viewall:hover {
      background: linear-gradient(white, white),
                  linear-gradient(135deg, #6B26D9 0%, #0000FF 100%);
      background-origin: border-box;
      background-clip: padding-box, border-box;
      border: 2px solid transparent;
      color: #000;
      background-image: linear-gradient(#f5f5f5, #f5f5f5), linear-gradient(135deg, #6B26D9 0%, #0000FF 100%);
    }
    @media (min-width: 768px) {
      .snapstyle-btn-viewall {
        width: fit-content;
        padding: 8px 24px;
      }
    }

    /* ═══════════════════════════════
       HISTORY SCREEN (Full)
       ═══════════════════════════════ */

    .snapstyle-history-screen {
      padding: 50px 0 0 0;
      display: flex;
      flex-direction: column;
      flex: 1;
    }
    .snapstyle-history-header {
      padding: 0 24px 16px;
    }
    .snapstyle-history-screen-title {
      font-size: 22px;
      font-weight: 600;
      line-height: 28px;
      color: #000;
      margin: 0 0 8px 0;
    }
    .snapstyle-history-screen-subtitle {
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0.25px;
      color: #49454F;
      margin: 0;
    }
    .snapstyle-history-full-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      padding: 0 24px;
      flex: 1;
      align-content: start;
    }
    @media (min-width: 768px) {
      .snapstyle-history-full-grid {
        grid-template-columns: repeat(4, 1fr);
        gap: 12px;
      }
    }
    .snapstyle-history-full-item {
      aspect-ratio: 1;
      border-radius: 10px;
      overflow: hidden;
      cursor: pointer;
      transition: transform 0.2s;
    }
    .snapstyle-history-full-item:hover {
      transform: scale(1.03);
    }
    .snapstyle-history-full-item img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .snapstyle-history-actions {
      padding: 24px;
    }
    @media (min-width: 768px) {
      .snapstyle-history-actions {
        display: flex;
        justify-content: center;
      }
    }
    .snapstyle-btn-goback {
      width: 100%;
      height: 46px;
      padding: 8px;
      background: white;
      border: 2px solid transparent;
      background-image: linear-gradient(white, white), linear-gradient(135deg, #6B26D9 0%, #0000FF 100%);
      background-origin: border-box;
      background-clip: padding-box, border-box;
      border-radius: 100px;
      color: #000;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
      cursor: pointer;
      transition: all 0.2s;
    }
    .snapstyle-btn-goback:hover {
      background: linear-gradient(white, white),
                  linear-gradient(135deg, #6B26D9 0%, #0000FF 100%);
      background-origin: border-box;
      background-clip: padding-box, border-box;
      border: 2px solid transparent;
      color: #000;
      background-image: linear-gradient(#f5f5f5, #f5f5f5), linear-gradient(135deg, #6B26D9 0%, #0000FF 100%);
    }
    @media (min-width: 768px) {
      .snapstyle-btn-goback {
        width: fit-content;
        padding: 8px 24px;
      }
    }

    /* ═══════════════════════════════
       PREVIEW SCREEN
       ═══════════════════════════════ */

    .snapstyle-preview-screen {
      padding: 50px 0 0 0;
      display: flex;
      flex-direction: column;
      flex: 1;
    }
    .snapstyle-preview-image-container {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 24px;
      min-height: 300px;
    }
    @media (min-width: 768px) {
      .snapstyle-preview-image-container {
        min-height: 400px;
      }
    }
    .snapstyle-preview-image-container img {
      max-width: 100%;
      max-height: 500px;
      object-fit: contain;
      border-radius: 6px;
    }
    @media (min-width: 768px) {
      .snapstyle-preview-image-container img {
        max-height: 600px;
      }
    }
    .snapstyle-preview-actions {
      display: flex;
      gap: 12px;
      padding: 16px 24px;
      align-items: center;
    }
    @media (min-width: 768px) {
      .snapstyle-preview-actions {
        margin: 0 auto;
        width: 100%;
      }
    }
    .snapstyle-btn-choose-another {
      flex: 1;
      height: 46px;
      padding: 8px 21px;
      background: white;
      border: 1px solid #DED8E1;
      border-radius: 100px;
      color: #000;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
      cursor: pointer;
      transition: background 0.2s;
    }
    .snapstyle-btn-choose-another:hover {
      background: #f5f5f5;
    }
    .snapstyle-btn-tryon {
      flex: 1;
      height: 46px;
      padding: 8px;
      background: #863AFF;
      border: none;
      border-radius: 100px;
      color: white;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
      cursor: pointer;
      transition: background 0.2s;
    }
    .snapstyle-btn-tryon:hover {
      background: #7330e0;
      border: none;
      color: white;
    }
    .snapstyle-btn-tryon:disabled,
    .snapstyle-btn-choose-another:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* ═══════════════════════════════
       LOADING SCREEN
       ═══════════════════════════════ */

    .snapstyle-loading-screen {
      position: relative;
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 400px;
      overflow: hidden;
      border-radius: 12px;
    }
    @media (min-width: 768px) {
      .snapstyle-loading-screen {
        min-height: 500px;
      }
    }
    .snapstyle-loading-bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      filter: blur(6px);
      opacity: 1;
      z-index: 1;
    }
    .snapstyle-loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.45);
      display: flex;
      z-index: 2;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 24px;
    }
    .snapstyle-loading-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      background: #ffffff;
      border-radius: 20px;
      padding: 28px 36px 24px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(134,58,255,0.08);
    }

    /* ── AI Shimmer Loader ── */
    .snapstyle-ai-loader {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
      width: 160px;
    }
    .snapstyle-ai-loader-icon {
      animation: snapstyle-icon-pulse 1.8s ease-in-out infinite;
    }
    @keyframes snapstyle-icon-pulse {
      0%, 100% { opacity: 1;   transform: scale(1);    filter: drop-shadow(0 0 0px #863AFF); }
      50%       { opacity: 0.8; transform: scale(1.25); filter: drop-shadow(0 0 10px #863AFF); }
    }
    .snapstyle-shimmer-bars {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100%;
    }
    .snapstyle-shimmer-bar {
      height: 8px;
      border-radius: 99px;
      background: linear-gradient(90deg, #ede9fe 25%, #c4b5fd 50%, #ede9fe 75%);
      background-size: 300% 100%;
      animation: snapstyle-shimmer 1.6s ease-in-out infinite;
    }
    .snapstyle-shimmer-bar--lg { width: 100%; animation-delay: 0s;    }
    .snapstyle-shimmer-bar--md { width: 75%;  animation-delay: 0.15s; }
    .snapstyle-shimmer-bar--sm { width: 50%;  animation-delay: 0.3s;  }
    @keyframes snapstyle-shimmer {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    /* Progress Bar */
    .snapstyle-progress-container {
      width: 140px;
      height: 3px;
      background: rgba(222, 216, 225, 0.6);
      border-radius: 28px;
      overflow: hidden;
      margin-top: 4px;
    }
    .snapstyle-progress-bar {
      height: 3px;
      display: block !important;
      background: linear-gradient(90deg, #863AFF 0%, #4400cc 100%);
      border-radius: 2px;
      transition: width 0.5s ease;
      width: 0%;
    }
    .snapstyle-progress-text {
      font-size: 20px;
      font-weight: 700;
      line-height: 28px;
      letter-spacing: 0.15px;
      color: #863AFF;
      margin: 0;
    }
    .snapstyle-progress-label {
      font-size: 12px;
      font-weight: 500;
      line-height: 16px;
      letter-spacing: 0.5px;
      color: rgba(98, 91, 113, 0.9);
      margin: 0;
      text-transform: uppercase;
    }

    /* ═══════════════════════════════
       RESULT SCREEN
       ═══════════════════════════════ */

    .snapstyle-result-screen {
      padding: 50px 0 0 0;
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    /* Compare / Your Try-on Tabs */
    .snapstyle-tabs {
      display: flex;
      align-self: center;
      background: #F3EDF7;
      border-radius: 4px;
      padding: 2px;
      margin-bottom: 16px;
    }
    .snapstyle-tab {
      padding: 6px 8px;
      border-radius: 5px;
      background: transparent;
      border: none;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
      color: #000;
      cursor: pointer;
      transition: all 0.2s;
    }
    .snapstyle-tab:hover {
      background: #e1e1e1;
      border: none;
    }
    .snapstyle-tab.active {
      background: white;
      border: 1px solid #DED8E1;
    }

    /* Result Columns — stacks on mobile, side-by-side on desktop */
    .snapstyle-result-content {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }
    .snapstyle-result-image-area {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
    .snapstyle-result-bottom {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    @media (min-width: 768px) {
      .snapstyle-result-content {
        padding: 0 48px;
        gap: 16px;
      }
      .snapstyle-result-image-area {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .snapstyle-result-bottom {
        gap: 16px;
      }
      .snapstyle-result-actions {
        padding: 0 !important;
      }
    }

    /* Compare Slider */
    .snapstyle-compare-container {
      position: relative;
      overflow: hidden;
      margin: 0 16px;
      border-radius: 6px;
      user-select: none;
      -webkit-user-select: none;
      touch-action: none;
    }
    @media (min-width: 768px) {
      .snapstyle-compare-container {
        margin: 0;
        max-width: 480px;
      }
    }
    /* Result image — sizes the container naturally */
    .snapstyle-compare-result {
      display: block;
      width: 100%;
      height: auto;
      pointer-events: none;
    }
    @media (min-width: 768px) {
      .snapstyle-compare-result {
        max-height: 580px;
        width: auto;
        max-width: 100%;
      }
    }
    /* Original overlay — clips from the right via overflow:hidden + variable width */
    .snapstyle-compare-original {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      overflow: hidden;
      pointer-events: none;
    }
    /* Original image — pinned top-left, matches container height, natural width */
    .snapstyle-compare-original img {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: auto;
      max-width: none;
      pointer-events: none;
    }
    .snapstyle-compare-line {
      position: absolute;
      top: 0;
      width: 3px;
      height: 100%;
      background: white;
      cursor: ew-resize;
      z-index: 5;
    }
    .snapstyle-compare-handle {
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 34px;
      height: 34px;
      background: linear-gradient(156deg, #6B26D9 0%, #0000FF 100%);
      border-radius: 50%;
      box-shadow: 0 4px 22px rgba(0,0,0,0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: ew-resize;
      z-index: 6;
    }
    .snapstyle-compare-handle svg {
      width: 18px;
      height: 18px;
    }

    /* Full Try-on View */
    .snapstyle-tryon-full {
      margin: 0 16px;
      border-radius: 6px;
      overflow: hidden;
    }
    @media (min-width: 768px) {
      .snapstyle-tryon-full {
        margin: 0;
        width: 480px;
        height: 580px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
    .snapstyle-tryon-full img {
      width: 100%;
      height: auto;
      display: block;
    }
    @media (min-width: 768px) {
      .snapstyle-tryon-full img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    /* Thumbnail Wrapper (scroll + arrows) */
    .snapstyle-thumb-wrapper {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 0 4px;
      position: relative;
    }
    @media (min-width: 768px) {
      .snapstyle-thumb-wrapper {
        justify-content: center;
        padding: 0;
        gap: 18px;
      }
    }
    .snapstyle-thumb-arrow {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 1px solid #DED8E1;
      background: white;
      font-size: 18px;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      transition: background 0.15s, opacity 0.2s;
      color: #322F35;
      z-index: 2;
    }
    .snapstyle-thumb-arrow:hover {
      background: #f5f5f5;
    }
    @media (min-width: 768px) {
      .snapstyle-thumb-arrow {
        width: 32px;
        height: 32px;
      }
    }

    /* Thumbnail Strip */
    .snapstyle-thumb-strip {
      display: flex;
      gap: 8px;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      padding: 8px 0;
      align-items: center;
      flex: 1;
      min-width: 0;
    }
    @media (min-width: 768px) {
      .snapstyle-thumb-strip {
        gap: 12px;
      }
    }
    .snapstyle-thumb-strip::-webkit-scrollbar {
      display: none;
    }
    .snapstyle-thumb-strip {
      scrollbar-width: none;
    }
    .snapstyle-thumb-item {
      scroll-snap-align: start;
      flex-shrink: 0;
      width: 64px;
      height: 64px;
      border-radius: 6px;
      overflow: hidden;
      cursor: pointer;
      border: 1px solid transparent;
      position: relative;
      transition: all 0.2s;
    }
    .snapstyle-thumb-item.active {
      border-color: #863AFF;
      box-shadow: 0 0 0 1px #863AFF;
    }
    .snapstyle-thumb-item:hover {
      border-color: #aaa;
    }
    .snapstyle-thumb-item img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    /* Result Actions */
    .snapstyle-result-actions {
      display: flex;
      gap: 12px;
      padding: 16px 12px;
      align-items: center;
    }
    @media (min-width: 768px) {
      .snapstyle-result-actions {
        gap: 24px;
        padding: 0;
      }
    }
    @media (max-width: 375px) {
      .snapstyle-result-actions {
        flex-direction: column;
      }
      .snapstyle-preview-actions{
        flex-direction: column;
      }
      .snapstyle-btn-tryon {
        width: -webkit-fill-available;
      }
      .snapstyle-btn-choose-another {
        width: -webkit-fill-available;
      }
      .snapstyle-btn-retry{
        width: -webkit-fill-available;
      }
      .snapstyle-btn-addcart{
        width: -webkit-fill-available;
      }
    }
    .snapstyle-btn-retry {
      flex: 1;
      height: 46px;
      padding: 8px 21px;
      background: white;
      border: 1px solid #DED8E1;
      border-radius: 100px;
      color: #000;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      transition: background 0.2s;
    }
    .snapstyle-btn-retry:hover {
      background: #f5f5f5;
    }
    .snapstyle-btn-retry svg {
      width: 16px;
      height: 16px;
    }
    .snapstyle-btn-addcart {
      flex: 1;
      height: 46px;
      padding: 8px;
      background: #863AFF;
      border: none;
      border-radius: 100px;
      color: white;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      transition: background 0.2s;
    }
    .snapstyle-btn-addcart:hover {
      background: #7330e0;
      color: white;
      border: none;
    }
    .snapstyle-btn-addcart svg {
      width: 16px;
      height: 16px;
    }

    /* ═══════════════════════════════
       FOOTER
       ═══════════════════════════════ */

    .snapstyle-footer {
      padding: 10px;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: auto;
    }
    .snapstyle-footer-logo-mobile {
      height: 20px;
      width: auto;
    }
    .snapstyle-footer-logo-desktop {
      display: none;
    }
    @media (min-width: 768px) {
      .snapstyle-footer-logo-mobile {
        display: none;
      }
      .snapstyle-footer-logo-desktop {
        display: block;
        width: auto;
        height: auto;
      }
    }

    /* ═══════════════════════════════
       ERROR STATE
       ═══════════════════════════════ */

    .snapstyle-error-msg {
      padding: 24px;
      text-align: center;
      color: #dc2626;
      font-size: 14px;
    }


    /* ═══════════════════════════════
       UPLOAD LOADING SCREEN
       ═══════════════════════════════ */

    .snapstyle-upload-loading-screen {
      position: relative;
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 400px;
      overflow: hidden;
      border-radius: 12px;
      background: linear-gradient(160deg, #f8f4ff 0%, #eef3ff 100%);
    }
    @media (min-width: 768px) {
      .snapstyle-upload-loading-screen {
        min-height: 500px;
      }
    }
    .snapstyle-upload-loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 24px;
    }
    .snapstyle-upload-loading-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
    }
    .snapstyle-upload-cloud-anim {
      position: relative;
      width: 80px;
      height: 80px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .snapstyle-cloud-icon {
      width: 64px;
      height: 64px;
      overflow: visible;
    }
    /* Rotating dashed rings */
    .snapstyle-cloud-ring-1 {
      transform-origin: 32px 32px;
      animation: snapstyle-ring-cw 4s linear infinite;
    }
    .snapstyle-cloud-ring-2 {
      transform-origin: 32px 32px;
      animation: snapstyle-ring-ccw 3s linear infinite;
    }
    @keyframes snapstyle-ring-cw {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes snapstyle-ring-ccw {
      from { transform: rotate(0deg); }
      to   { transform: rotate(-360deg); }
    }
    /* Bouncing upload arrow inside cloud */
    .snapstyle-cloud-arrow {
      animation: snapstyle-arrow-bounce 1.2s ease-in-out infinite;
    }
    @keyframes snapstyle-arrow-bounce {
      0%, 100% { transform: translateY(0); }
      50%       { transform: translateY(-4px); }
    }
    /* Three pulsing dots below the icon */
    .snapstyle-upload-dots {
      display: flex;
      gap: 6px;
      margin-top: 4px;
    }
    .snapstyle-upload-dots span {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #863AFF;
      animation: snapstyle-dot-pulse 1.4s ease-in-out infinite;
    }
    .snapstyle-upload-dots span:nth-child(2) {
      animation-delay: 0.2s;
    }
    .snapstyle-upload-dots span:nth-child(3) {
      animation-delay: 0.4s;
    }
    @keyframes snapstyle-dot-pulse {
      0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
      40%            { transform: scale(1);   opacity: 1;   }
    }
    .snapstyle-upload-loading-title {
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
      letter-spacing: 0.15px;
      color: #000;
      margin: 0;
      text-align: center;
    }
    .snapstyle-upload-loading-subtitle {
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0.4px;
      color: #625B71;
      margin: 0;
      text-align: center;
      max-width: 220px;
    }

    /* ── Error Toast Overlay ── */
    .snapstyle-error-toast {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 30;
      border-radius: 16px;
      animation: snapstyle-fade-in 0.2s ease;
    }
    @keyframes snapstyle-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    .snapstyle-error-toast-card {
      background: white;
      border-radius: 16px;
      padding: 28px 24px;
      max-width: 300px;
      width: 85%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
      position: relative;
      text-align: center;
    }
    .snapstyle-error-toast-close {
      position: absolute;
      top: 8px;
      right: 12px;
      background: none;
      border: none;
      font-size: 22px;
      color: #999;
      cursor: pointer;
      line-height: 1;
      padding: 4px;
    }
    .snapstyle-error-toast-close:hover {
      color: #333;
    }
    .snapstyle-error-toast-icon {
      width: 40px;
      height: 40px;
    }
    .snapstyle-error-toast-icon svg {
      width: 40px;
      height: 40px;
    }
    .snapstyle-error-toast-msg {
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: #333;
      margin: 0;
    }
    .snapstyle-error-toast-retry {
      width: 100%;
      height: 40px;
      margin-top: 4px;
      background: #863AFF;
      border: none;
      border-radius: 100px;
      color: white;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0.1px;
      cursor: pointer;
      transition: background 0.2s;
    }
    .snapstyle-error-toast-retry:hover {
      background: #7330e0;
    }

    /* ── Hidden file inputs ── */
    .snapstyle-hidden-input {
      display: none;
    }
  `;

  document.head.appendChild(style);
}
